import type { System } from '../../odie';

import StateMachine from '../../lib/pattern/StateMachine';
import { PhaseComponent } from '../components/PhaseComponent';
import type { PhaseId } from '../defs/types';
import type { GameScene } from '../GameScene';
import { ActivePhase } from './phases/ActivePhase';
import { FallPhase } from './phases/FallPhase';
import { InputPhase } from './phases/InputPhase';
import { ShufflePhase } from './phases/ShufflePhase';
import { TransfigurationPhase } from './phases/TransfigurationPhase';

// types
//-----------------------------------------------------------------------------
export interface IPhase {
  step?: () => void;
}

/*
    manages game phases (singleton)
    phase flow
        fall (start)
            if blocks can fall, fall blocks, then: active
            else: shuffle
        active
            when activations = 0: fall
        shuffle
            if user not stuck: input
            else if user stuck and after resolved: input
            else if not resolved, attack all: action
        input
            when activations > 0: active
*/
export class PhaseSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'phase';

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene?: GameScene;
  // state
  private _shufflePhase!: ShufflePhase;
  private _inputPhase!: InputPhase;
  private _fallPhase!: FallPhase;
  private _activePhase!: ActivePhase;
  private _transfigurationPhase!: TransfigurationPhase;
  private _stateMachine!: StateMachine<PhaseId>;

  // properties
  //-------------------------------------------------------------------------
  public get phase(): PhaseId {
    return this._stateMachine.state;
  }

  public set phase(phaseId: PhaseId) {
    this._stateMachine.transition(phaseId);
    if (this.component) {
      this.component.phase = phaseId;
    }
  }

  public get component(): PhaseComponent | null {
    if (!this.scene) return null;
    return this.scene.sessionEntity.c.phase;
  }

  // impl
  //-------------------------------------------------------------------------
  public init() {
    // create phases
    this._shufflePhase = new ShufflePhase(this);
    this._inputPhase = new InputPhase(this);
    this._fallPhase = new FallPhase(this);
    this._activePhase = new ActivePhase(this);
    this._transfigurationPhase = new TransfigurationPhase(this);

    // create state machine
    this._stateMachine = new StateMachine<PhaseId>({
      none: {},
      active: this._activePhase,
      fall: this._fallPhase,
      input: this._inputPhase,
      shuffle: this._shufflePhase,
      transfiguration: this._transfigurationPhase,
    });
  }

  public awake() {
    if (!this.scene) return;

    // reset state
    this._stateMachine.reset();

    // enter fall state
    this._stateMachine.transition('fall');

    // Start with input disabled - this will be re-enabled later when
    // the PowerBlockSystem is done applying extra powerups, only if not using save state
    if (!this.scene.usingSaveState) {
      this.setInputDisable(true);
    }
  }

  public update(): void {
    // step current phase
    (this._stateMachine.observer as IPhase).step?.();
  }

  // api
  //-------------------------------------------------------------------------
  public setInputDisable(v: boolean) {
    this._inputPhase.disabled = v;
  }

  public waitForPhase(phaseId: PhaseId) {
    return new Promise<void>((resolve) => {
      const listener = (
        e:
          | {
              previousPhase: PhaseId | null;
              newPhase: PhaseId;
            }
          | undefined,
      ) => {
        if (e?.newPhase !== phaseId) return;
        this._stateMachine.onTransitioned.unsubscribe(listener);
        resolve();
      };
      this._stateMachine.onTransitioned.subscribe(listener);
    });
  }
}
